import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';

const PercentageCalculator = () => {
  // Basic percentage calculator
  const [basicValue, setBasicValue] = useState<string>('100');
  const [basicPercent, setBasicPercent] = useState<string>('10');
  const [basicResult, setBasicResult] = useState<string>('');
  
  // Percentage change calculator
  const [oldValue, setOldValue] = useState<string>('100');
  const [newValue, setNewValue] = useState<string>('120');
  const [changeResult, setChangeResult] = useState<string>('');
  
  // Percent of value calculator
  const [partValue, setPartValue] = useState<string>('20');
  const [wholeValue, setWholeValue] = useState<string>('80');
  const [percentResult, setPercentResult] = useState<string>('');
  
  // Add ref for the calculator container
  const calculatorRef = useRef<HTMLDivElement>(null);
  
  // Add effect to prevent scrolling on focus
  useEffect(() => {
    // Function to prevent scroll on focus
    const preventScrollOnFocus = (e: Event) => {
      e.preventDefault();
      // Keep focus but prevent scrolling
      if (calculatorRef.current) {
        calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    };

    // Get all focusable elements within the calculator
    const focusableElements = calculatorRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    // Add event listeners to prevent scroll
    focusableElements?.forEach((el) => {
      el.addEventListener('focus', preventScrollOnFocus);
    });

    // Clean up
    return () => {
      focusableElements?.forEach((el) => {
        el.removeEventListener('focus', preventScrollOnFocus);
      });
    };
  }, [calculatorRef]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Calculate basic percentage
  const calculateBasicPercentage = () => {
    try {
      const value = parseFloat(basicValue);
      const percent = parseFloat(basicPercent);
      
      if (isNaN(value) || isNaN(percent)) {
        toast({
          title: "Invalid input",
          description: "Please enter valid numbers",
          variant: "destructive"
        });
        return;
      }
      
      const result = (value * percent) / 100;
      setBasicResult(`${percent}% of ${value} = ${result.toFixed(2)}`);
      
      toast({
        title: "Calculation complete",
        description: `${percent}% of ${value} = ${result.toFixed(2)}`
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };
  
  // Calculate percentage change
  const calculatePercentageChange = () => {
    try {
      const oldVal = parseFloat(oldValue);
      const newVal = parseFloat(newValue);
      
      if (isNaN(oldVal) || isNaN(newVal)) {
        toast({
          title: "Invalid input",
          description: "Please enter valid numbers",
          variant: "destructive"
        });
        return;
      }
      
      if (oldVal === 0) {
        toast({
          title: "Invalid input",
          description: "Original value cannot be zero",
          variant: "destructive"
        });
        return;
      }
      
      const change = newVal - oldVal;
      const percentChange = (change / Math.abs(oldVal)) * 100;
      
      const changeType = percentChange >= 0 ? "increase" : "decrease";
      setChangeResult(`Percentage ${changeType}: ${Math.abs(percentChange).toFixed(2)}%`);
      
      toast({
        title: "Calculation complete",
        description: `Percentage ${changeType}: ${Math.abs(percentChange).toFixed(2)}%`
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };
  
  // Calculate what percent X is of Y
  const calculatePercentOfValue = () => {
    try {
      const part = parseFloat(partValue);
      const whole = parseFloat(wholeValue);
      
      if (isNaN(part) || isNaN(whole)) {
        toast({
          title: "Invalid input",
          description: "Please enter valid numbers",
          variant: "destructive"
        });
        return;
      }
      
      if (whole === 0) {
        toast({
          title: "Invalid input",
          description: "Total value cannot be zero",
          variant: "destructive"
        });
        return;
      }
      
      const percent = (part / whole) * 100;
      setPercentResult(`${part} is ${percent.toFixed(2)}% of ${whole}`);
      
      toast({
        title: "Calculation complete",
        description: `${part} is ${percent.toFixed(2)}% of ${whole}`
      });
    } catch (error) {
      toast({
        title: "Calculation error",
        description: "An error occurred during calculation",
        variant: "destructive"
      });
    }
  };
  
  return (
    <div ref={calculatorRef}>
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid grid-cols-3 mb-4">
          <TabsTrigger value="basic">Basic Percentage</TabsTrigger>
          <TabsTrigger value="change">Percentage Change</TabsTrigger>
          <TabsTrigger value="percent">Percent of Value</TabsTrigger>
        </TabsList>
        
        <TabsContent value="basic">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="basicValue">Value</Label>
                  <Input
                    id="basicValue"
                    type="number"
                    value={basicValue}
                    onChange={(e) => setBasicValue(e.target.value)}
                    placeholder="Enter a value"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="basicPercent">Percentage (%)</Label>
                  <Input
                    id="basicPercent"
                    type="number"
                    value={basicPercent}
                    onChange={(e) => setBasicPercent(e.target.value)}
                    placeholder="Enter percentage"
                  />
                </div>
                
                <Button onClick={calculateBasicPercentage} className="w-full">Calculate</Button>
                
                {basicResult && (
                  <div className="mt-4 p-4 bg-muted rounded-md">
                    <p className="font-medium text-center">{basicResult}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="change">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="oldValue">Original Value</Label>
                  <Input
                    id="oldValue"
                    type="number"
                    value={oldValue}
                    onChange={(e) => setOldValue(e.target.value)}
                    placeholder="Enter original value"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="newValue">New Value</Label>
                  <Input
                    id="newValue"
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Enter new value"
                  />
                </div>
                
                <Button onClick={calculatePercentageChange} className="w-full">Calculate Change</Button>
                
                {changeResult && (
                  <div className="mt-4 p-4 bg-muted rounded-md">
                    <p className="font-medium text-center">{changeResult}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="percent">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="partValue">Part Value</Label>
                  <Input
                    id="partValue"
                    type="number"
                    value={partValue}
                    onChange={(e) => setPartValue(e.target.value)}
                    placeholder="Enter part value"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="wholeValue">Total Value</Label>
                  <Input
                    id="wholeValue"
                    type="number"
                    value={wholeValue}
                    onChange={(e) => setWholeValue(e.target.value)}
                    placeholder="Enter total value"
                  />
                </div>
                
                <Button onClick={calculatePercentOfValue} className="w-full">Calculate Percentage</Button>
                
                {percentResult && (
                  <div className="mt-4 p-4 bg-muted rounded-md">
                    <p className="font-medium text-center">{percentResult}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PercentageCalculator;
